"use client";

import { useEffect, useState } from "react";
import { subscribeToToken } from "@/hooks/useClobWebSocket";

/**
 * Live prices for every market visible on the sports page, via CLOB
 * WebSocket. Walks all groups in `marketsByType`, collects every
 * `clobTokenId`, and subscribes once per token.
 *
 * Stores `bestAsk` snapped to 0.01 — matches the snap in
 * `useRealtimeMarketPrice.getBuyPrices` so the moneyline buttons and
 * the sidebar TradingPanel render identical numbers. When the
 * orderbook has no real ask (`bestAsk >= 1`) and no real bid, we
 * store 0 — Polymarket renders those degenerate side-markets as "0¢"
 * instead of falling back to gamma's stale `outcomePrices` mid (which
 * would read as "50¢").
 *
 * Returns the price map keyed by `clobTokenId`.
 */
export function useAllLivePrices(
  marketsByType: Record<string, { markets: Array<{ clobTokenIds?: string[] }> }> | null | undefined,
): Record<string, number> {
  const [allLivePrices, setAllLivePrices] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!marketsByType) return;

    const allTokenIds = new Set<string>();
    for (const group of Object.values(marketsByType)) {
      for (const market of group.markets) {
        for (const tid of market.clobTokenIds || []) {
          if (tid) allTokenIds.add(tid);
        }
      }
    }

    if (allTokenIds.size === 0) return;

    const unsubs: Array<() => void> = [];
    for (const tokenId of allTokenIds) {
      const unsub = subscribeToToken(tokenId, (assetId, bestBid, bestAsk) => {
        const askValid = bestAsk > 0 && bestAsk < 1;
        const bidValid = bestBid > 0 && bestBid < 1;
        const raw = askValid ? bestAsk : bidValid ? bestBid : -1;
        const price = raw < 0 ? 0 : Math.round(raw / 0.01) * 0.01;
        setAllLivePrices((prev) => (prev[assetId] === price ? prev : { ...prev, [assetId]: price }));
      });
      unsubs.push(unsub);
    }

    return () => unsubs.forEach((u) => u());
  }, [marketsByType]);

  return allLivePrices;
}
